import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Mail, Lock, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";

const AuthPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mode, setMode] = useState<"login" | "signup" | "forgot" | "reset">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Check URL/hash for password recovery parameter on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const type = params.get("type") || hashParams.get("type");
    if (type === "recovery") {
      setMode("reset");
    }
  }, []);

  // Redirect if already logged in (but bypass if currently recovering password)
  useEffect(() => {
    const isRecovery = window.location.search.includes("type=recovery") || window.location.hash.includes("type=recovery");
    if (user && !isRecovery && mode !== "reset") {
      navigate("/app");
    }
  }, [user, navigate, mode]);

  const getRedirectUrl = () => {
    const hostname = window.location.hostname;
    const url = (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0')
      ? 'http://localhost:8080/app' 
      : 'https://alvaru.com/app';
    console.log('Alvaru: Auth redirect URL:', url);
    return url;
  };

  const getRecoveryRedirectUrl = () => {
    const hostname = window.location.hostname;
    const url = (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0')
      ? 'http://localhost:8080/auth?type=recovery' 
      : 'https://alvaru.com/auth?type=recovery';
    console.log('Alvaru: Recovery redirect URL:', url);
    return url;
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: getRedirectUrl()
      }
    });
    if (error) console.error('Google sign in error:', error);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === "signup") {
        if (!email || !password) { toast.error("Please fill in all fields"); setLoading(false); return; }
        const { error } = await supabase.auth.signUp({ email, password });
        if (error) {
          toast.error(error.message);
        } else {
          toast.success("Account created! Check your email to confirm, then log in.");
          console.log('About to call subscribe-brevo for: ' + email);
          const { data, error } = await supabase.functions.invoke('subscribe-brevo', {
            body: { email, name: email.split('@')[0] }
          });
          console.log('Response: ' + JSON.stringify({ data, error }));
          setMode("login");
        }
      } else if (mode === "login") {
        if (!email || !password) { toast.error("Please fill in all fields"); setLoading(false); return; }
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) {
          toast.error(error.message);
        } else {
          toast.success("Welcome back!");
          navigate("/app");
        }
      } else if (mode === "forgot") {
        if (!email) { toast.error("Please enter your email address"); setLoading(false); return; }
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: getRecoveryRedirectUrl()
        });
        if (error) {
          toast.error(error.message);
        } else {
          toast.success("Password reset link sent! Check your email inbox.");
          setMode("login");
        }
      } else if (mode === "reset") {
        if (!newPassword) { toast.error("Please enter a new password"); setLoading(false); return; }
        const { error } = await supabase.auth.updateUser({ password: newPassword });
        if (error) {
          toast.error(error.message);
        } else {
          toast.success("Password updated successfully! Logging you in...");
          navigate("/app");
        }
      }
    } catch (err: any) {
      toast.error(err.message || "An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  if (user && mode !== "reset") return null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#030303] px-4 selection:bg-primary/30">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <motion.div 
            initial={{ y: -10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 mb-4 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 backdrop-blur-md"
          >
            <Sparkles className="w-5 h-5 text-primary" />
            <span className="font-heading text-lg font-bold text-primary tracking-tight">Alvaru AI</span>
          </motion.div>
          <h1 className="font-heading text-4xl font-bold text-white tracking-tight uppercase italic italic">
            {mode === "login" 
              ? "Welcome back" 
              : mode === "signup" 
              ? "Elevate Your Career" 
              : mode === "forgot" 
              ? "Reset Password" 
              : "New Password"}
          </h1>
          <p className="text-slate-400 text-sm mt-3 font-medium uppercase tracking-widest opacity-60">
            {mode === "login"
              ? "Sign in to access your dashboard"
              : mode === "signup"
              ? "Create an account to start optimising"
              : mode === "forgot"
              ? "Receive a secure recovery link"
              : "Enter your new credentials"}
          </p>
        </div>

        {/* Card */}
        <div className="bg-[#0b0b0c] border border-white/10 rounded-3xl p-8 space-y-6 shadow-2xl relative overflow-hidden">
          {/* Subtle flare */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 blur-[60px] rounded-full pointer-events-none" />
          
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            {mode !== "reset" && (
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-slate-500" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@email.com"
                    className="w-full bg-white/[0.03] border border-white/10 rounded-2xl pl-12 pr-4 py-4 text-sm focus:outline-none focus:border-primary/50 focus:bg-white/[0.05] placeholder:text-slate-600 transition-all text-white font-medium"
                  />
                </div>
              </div>
            )}

            {/* Password */}
            {(mode === "login" || mode === "signup") && (
              <div className="space-y-2">
                <div className="flex justify-between items-center px-1">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Secure Password</label>
                  {mode === "login" && (
                    <button
                      type="button"
                      onClick={() => setMode("forgot")}
                      className="text-[10px] font-bold text-primary hover:underline uppercase tracking-widest focus:outline-none"
                    >
                      Forgot Password?
                    </button>
                  )}
                </div>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-slate-500" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full bg-white/[0.03] border border-white/10 rounded-2xl pl-12 pr-4 py-4 text-sm focus:outline-none focus:border-primary/50 focus:bg-white/[0.05] placeholder:text-slate-600 transition-all text-white font-medium"
                  />
                </div>
              </div>
            )}

            {/* New Password (Reset Mode) */}
            {mode === "reset" && (
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">New Secure Password</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-slate-500" />
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full bg-white/[0.03] border border-white/10 rounded-2xl pl-12 pr-4 py-4 text-sm focus:outline-none focus:border-primary/50 focus:bg-white/[0.05] placeholder:text-slate-600 transition-all text-white font-medium"
                  />
                </div>
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary/90 text-black font-bold py-7 rounded-2xl shadow-xl shadow-primary/20 transition-all hover:scale-[1.01] active:scale-95 group overflow-hidden relative"
              disabled={loading}
            >
              <div className="relative flex items-center justify-center gap-2">
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Sparkles className="w-5 h-5 transition-transform group-hover:rotate-12" />
                )}
                <span className="uppercase tracking-widest text-sm">
                  {loading ? "Authorizing..." : mode === "login" ? "Sign In" : mode === "signup" ? "Start Journey" : mode === "forgot" ? "Send Reset Link" : "Update Password"}
                </span>
              </div>
            </Button>
          </form>

          {(mode === "login" || mode === "signup") && (
            <>
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-white/10" />
                </div>
                <div className="relative flex justify-center text-[10px] uppercase tracking-widest">
                  <span className="bg-[#0b0b0c] px-2 text-slate-500">Or continue with</span>
                </div>
              </div>

              <Button
                type="button"
                onClick={signInWithGoogle}
                className="w-full flex items-center justify-center gap-2 bg-white/[0.03] border-white/10 hover:bg-white/[0.05] text-white py-7 rounded-2xl transition-all shadow-xl shadow-black/20"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
                <span className="uppercase tracking-widest text-sm font-bold">
                  {mode === "login" ? "Sign in with Google" : "Sign up with Google"}
                </span>
              </Button>
            </>
          )}

          <div className="text-center pt-2">
            <button
              onClick={() => {
                if (mode === "forgot" || mode === "reset") {
                  setMode("login");
                } else {
                  setMode(mode === "login" ? "signup" : "login");
                }
              }}
              className="text-[11px] font-bold text-slate-500 hover:text-primary transition-colors uppercase tracking-[0.2em]"
            >
              {mode === "login"
                ? "Don't have an account? Sign up"
                : mode === "signup"
                ? "Already have an account? Sign in"
                : "Back to Sign In"}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AuthPage;
